import { useAsyncData } from '#imports'
import { computed } from 'vue'

import type { DateTime } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

import type { Customer, Orderline, Product } from '~/api/customer-request/types'
import type {
  Contact,
  HelpRequest,
  ProductReturn,
} from '~/api/seller-after-sales/types'
import { useOrderlinePublicIdFromRoute } from '~/scopes/customer-care/pages/request/composables/useOrderlinePublicIdFromRoute'

function getMostRecentDate(productReturn: ProductReturn) {
  const filteredDates = [
    productReturn.creationDate,
    productReturn.dateDelivered,
    productReturn.datePickedUp,
  ].filter((date) => date) as DateTime[]
  const dates = filteredDates.map((date) => new Date(date).getTime())

  return new Date(Math.max(...dates))
}

export function useOrderlineDetails() {
  const $httpFetch = useHttpFetch()
  const orderlinePublicId = useOrderlinePublicIdFromRoute()
  const { openErrorToast } = useTheToast()

  const {
    data: orderline,
    status: orderlineStatus,
    refresh: refreshOrderline,
  } = useAsyncData<Orderline>(
    `orderline-${orderlinePublicId.value}`,
    () =>
      $httpFetch('/bm/customer_request/orderlines/:orderlineId', {
        pathParams: {
          orderlineId: orderlinePublicId.value,
        },
      }),
    {
      dedupe: 'defer',
      watch: [orderlinePublicId],
    },
  )

  const {
    data: product,
    status: productStatus,
    refresh: refreshProduct,
  } = useAsyncData<Product>(
    `orderline-product-${orderlinePublicId.value}`,
    () =>
      $httpFetch('/bm/customer_request/orderlines/:orderlineId/product', {
        pathParams: {
          orderlineId: orderlinePublicId.value,
        },
      }),
    {
      dedupe: 'defer',
      watch: [orderlinePublicId],
    },
  )

  const {
    data: productReturns,
    status: productReturnsStatus,
    refresh: refreshProductReturns,
  } = useAsyncData<ProductReturn[]>(
    `orderline-product-returns-${orderlinePublicId.value}`,
    () =>
      $httpFetch(
        '/seller-after-sales/api/v1/orderlines/:orderlinePublicId/product_returns',
        {
          pathParams: {
            orderlinePublicId: orderlinePublicId.value,
          },
        },
      ),
    {
      dedupe: 'defer',
      watch: [orderlinePublicId],
    },
  )

  const {
    data: customer,
    status: customerStatus,
    refresh: refreshCustomer,
  } = useAsyncData<Customer>(
    `orderline-customer-${orderlinePublicId.value}`,
    () =>
      $httpFetch('/bm/customer_request/orderlines/:orderlineId/customer', {
        pathParams: {
          orderlineId: orderlinePublicId.value,
        },
      }),
    {
      dedupe: 'defer',
      watch: [orderlinePublicId],
    },
  )

  const {
    data: attachments,
    status: attachmentsStatus,
    refresh: refreshAttachments,
  } = useAsyncData(
    `orderline-attachments-${orderlinePublicId.value}`,
    () =>
      $httpFetch(
        '/seller-after-sales/api/v1/orderlines/:orderlinePublicId/attachments',
        {
          pathParams: {
            orderlinePublicId: orderlinePublicId.value,
          },
        },
      ),
    {
      transform: (data) => {
        const count = data.content.reduce((acc, date) => {
          return acc + date.files.length
        }, 0)

        return {
          content: data.content,
          metadata: {
            count,
          },
        }
      },
      dedupe: 'defer',
      watch: [orderlinePublicId],
    },
  )

  const {
    data: contacts,
    status: contactsStatus,
    refresh: refreshContacts,
  } = useAsyncData(
    `orderline-contacts-${orderlinePublicId.value}`,
    () =>
      $httpFetch(
        '/seller-after-sales/composition_api/v1/orderlines/:orderlinePublicId/customer_issues',
        {
          pathParams: {
            orderlinePublicId: orderlinePublicId.value,
          },
        },
      ),
    {
      transform: (helpRequests: HelpRequest[]) => {
        return helpRequests.reduce((allContacts: Contact[], helpRequest) => {
          return helpRequest.contacts
            ? allContacts.concat(helpRequest.contacts)
            : allContacts
        }, [])
      },
      dedupe: 'defer',
      watch: [orderlinePublicId],
    },
  )

  const productDetailsStatus = computed(() => {
    if (
      orderlineStatus.value === 'pending' ||
      productStatus.value === 'pending'
    )
      return 'pending'

    if (orderlineStatus.value === 'idle' || productStatus.value === 'idle')
      return 'idle'

    if (orderlineStatus.value === 'error' || productStatus.value === 'error')
      return 'error'

    return 'success'
  })

  const headerStatus = computed(() => {
    if (
      orderlineStatus.value === 'pending' ||
      productStatus.value === 'pending' ||
      productReturnsStatus.value === 'pending'
    )
      return 'pending'

    if (
      orderlineStatus.value === 'idle' ||
      productStatus.value === 'idle' ||
      productReturnsStatus.value === 'idle'
    )
      return 'idle'

    if (
      orderlineStatus.value === 'error' ||
      productStatus.value === 'error' ||
      productReturnsStatus.value === 'error'
    ) {
      openErrorToast()

      return 'error'
    }

    return 'success'
  })

  const customerDetailsStatus = computed(() => {
    if (
      orderlineStatus.value === 'pending' ||
      customerStatus.value === 'pending'
    )
      return 'pending'

    if (orderlineStatus.value === 'idle' || customerStatus.value === 'idle')
      return 'idle'

    if (orderlineStatus.value === 'error' || customerStatus.value === 'error')
      return 'error'

    return 'success'
  })

  function refreshProductDetails() {
    if (orderlineStatus.value !== 'success') refreshOrderline()

    if (productStatus.value !== 'success') refreshProduct()
  }

  function refreshCustomerDetails() {
    if (orderlineStatus.value !== 'success') refreshOrderline()

    if (customerStatus.value !== 'success') refreshCustomer()
  }

  const mostRecentProductReturn = computed(() => {
    if (!productReturns.value) return null

    return productReturns.value.reduce<ProductReturn | null>(
      (mostRecent, productReturn) => {
        if (!mostRecent) return productReturn
        if (getMostRecentDate(productReturn) > getMostRecentDate(mostRecent)) {
          return productReturn
        }

        return mostRecent
      },
      null,
    )
  })

  return {
    productDetails: {
      data: {
        orderline,
        product,
      },
      status: productDetailsStatus,
      refresh: refreshProductDetails,
    },
    orderDetails: {
      data: {
        orderline,
      },
      status: orderlineStatus,
      refresh: refreshOrderline,
    },
    returnsDetails: {
      data: {
        mostRecentProductReturn,
        productReturns,
      },
      status: productReturnsStatus,
      refresh: refreshProductReturns,
    },
    customerDetails: {
      data: {
        orderline,
        customer,
      },
      status: customerDetailsStatus,
      refresh: refreshCustomerDetails,
    },
    attachments: {
      data: attachments,
      status: attachmentsStatus,
      refresh: refreshAttachments,
    },
    events: {
      data: contacts,
      status: contactsStatus,
      refresh: refreshContacts,
    },
    header: {
      data: {
        orderline,
        product,
        mostRecentProductReturn,
      },
      status: headerStatus,
    },
  }
}
